import React, { FC, useEffect } from 'react';
import PaginationWithDateRange, { PaginationWithDateRangeProps } from 'components/Paginations/PaginationWithDateRange';
import PaginationContainer from 'components/Paginations/PaginationContainer';
import { DEFAULT_PAGINATION_VALUE, PaginationLabels, Value as PaginationValue } from 'components/Paginations';
import { DEFAULT_DATE_RANGE_PICKER_VALUE, DateRange } from 'components/DateRangePicker';
import { mergeTwoObjects } from 'utils';
import BasicTable, { BasicTableProps, FilterOptions, RequestFilters, SelectedFind, SortValue } from './BasicTable';
import TableWrapper, { TableWrapperProps } from './TableWrapper';

export type TableWithDateRangePaginationChanges = RequestFilters & {
  pagination?: PaginationValue;
  dates?: DateRange;
  accountType?: string;
};

export type TableWithDateRangePaginationProps = Pick<
  BasicTableProps,
  'cellRenderer' | 'data' | 'columns' | 'tableLayout' | 'stickyHeader' | 'stickyHeaderOffsets' | 'hasNoFilters'
> & {
  tableWrapperProps?: TableWrapperProps;
  paginationLabels?: PaginationLabels;
  filtersData?: {
    find?: FilterOptions;
    count: number;
  };
  filters?: {
    order?: SortValue;
    find?: SelectedFind;
    dates?: DateRange;
    pagination?: PaginationValue;
    accountType?: string;
  };
  defaultFilters?: {
    order?: SortValue;
    find?: SelectedFind;
    dates?: DateRange;
    pagination?: PaginationValue;
    accountType?: string;
  };
  shouldApplyChangesOnMount?: boolean;
  shouldUseDefaultDatesOnMount?: boolean;
  isNotEndDateLimited?: boolean;
  applyChanges: (value: TableWithDateRangePaginationChanges) => void;
  hasFixedPagination?: boolean;
  dateFormat?: string;
};

const TableWithDateRangePagination: FC<TableWithDateRangePaginationProps> = ({
  data,
  columns,
  cellRenderer,
  tableWrapperProps,
  paginationLabels,
  filtersData,
  filters,
  defaultFilters,
  shouldApplyChangesOnMount,
  shouldUseDefaultDatesOnMount = true,
  applyChanges,
  isNotEndDateLimited,
  hasFixedPagination = true,
  dateFormat = 'DMY',
  tableLayout,
  stickyHeader,
  stickyHeaderOffsets,
  hasNoFilters,
}) => {
  const dateRangePickerProps = mergeTwoObjects(
    {
      value: filters?.dates,
      defaultValue: defaultFilters?.dates || DEFAULT_DATE_RANGE_PICKER_VALUE,
      isNotEndDateLimited,
      onChange: (value: DateRange) => applyChanges({ dates: value }),
      dateFormat,
    },
    {},
  );

  const accountTypeSelectProps = mergeTwoObjects(
    {
      value: filters?.accountType,
      onChange: (value: string) => applyChanges({ accountType: value }),
    },
    {},
  );

  const paginationProps = mergeTwoObjects(
    {
      accountTypeSelectProps,
      dateRangePickerProps,
      count: filtersData?.count,
      value: filters?.pagination,
      defaultValue: defaultFilters?.pagination || DEFAULT_PAGINATION_VALUE,
      paginationLabels: paginationLabels,
      onChange: (value: PaginationValue) => applyChanges({ pagination: value }),
    },
    {},
  ) as PaginationWithDateRangeProps;

  const tableProps = mergeTwoObjects(
    {
      data,
      columns,
      cellRenderer,
      filtersData,
      filters,
      defaultFilters,
      applyChanges: (value: RequestFilters) => applyChanges(value),
      tableLayout,
      stickyHeader,
      stickyHeaderOffsets,
      hasNoFilters,
    },
    {},
  ) as BasicTableProps;

  useEffect(() => {
    const shouldApply = typeof shouldApplyChangesOnMount === 'boolean' ? shouldApplyChangesOnMount : true;
    if (shouldApply) {
      applyChanges(
        mergeTwoObjects(filters || {}, {
          ...(shouldUseDefaultDatesOnMount ? { dates: dateRangePickerProps.defaultValue } : {}),
          pagination: paginationProps.defaultValue,
          ...(defaultFilters || {}),
        }),
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <TableWrapper {...(tableWrapperProps || {})}>
      <PaginationContainer top={0} isFixed={hasFixedPagination}>
        <PaginationWithDateRange {...paginationProps} />
      </PaginationContainer>
      <BasicTable {...tableProps} stickyHeader={hasFixedPagination || stickyHeader} shouldApplyChangesOnMount={false} />
    </TableWrapper>
  );
};

export default TableWithDateRangePagination;
